import type {
  AdviceReceipt,
  CharacterSnapshot,
  DecisionReceipt,
} from '../types/character.types';
function object(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function number(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
function strings(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
}
function nullableStrings(value: unknown): boolean {
  return value === null || value === undefined || strings(value);
}
export function parseAdvice(value: unknown): AdviceReceipt | null {
  if (
    !object(value) ||
    !object(value.command) ||
    !number(value.heroId) ||
    value.heroId <= 0 ||
    !number(value.ownedSequence) ||
    value.status !== 'accepted' ||
    typeof value.binding !== 'string' ||
    typeof value.acceptedUTC !== 'string' ||
    typeof value.acceptedSimTime !== 'string'
  )
    return null;
  const c = value.command;
  if (
    typeof c.commandId !== 'string' ||
    typeof c.instanceId !== 'string' ||
    !number(c.stageRevision) ||
    typeof c.suggestedActionId !== 'string' ||
    !nullableStrings(c.targetIds)
  )
    return null;
  return {
    ...structuredClone(value),
    command: { ...structuredClone(c), targetIds: c.targetIds ?? [] },
  } as unknown as AdviceReceipt;
}
export function parseDecision(value: unknown): DecisionReceipt | null {
  if (
    !object(value) ||
    !number(value.heroId) ||
    value.heroId <= 0 ||
    !number(value.fromRevision) ||
    !number(value.toRevision) ||
    value.toRevision <= value.fromRevision ||
    !nullableStrings(value.targetIds) ||
    !nullableStrings(value.reasons) ||
    (value.suggestedActionId !== undefined && (typeof value.suggestedActionId !== 'string' || !/^[a-z_]{1,64}(?::[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12})?$/i.test(value.suggestedActionId)))
  )
    return null;
  for (const key of [
    'attemptId',
    'instanceId',
    'ownActionId',
    'chosenActionId',
    'reaction',
    'resolvedSimAt',
  ])
    if (typeof value[key] !== 'string') return null;
  if (
    value.adviceCommandId !== undefined &&
    typeof value.adviceCommandId !== 'string'
  )
    return null;
  return {
    ...structuredClone(value),
    targetIds: value.targetIds ?? [],
    reasons: value.reasons ?? [],
  } as unknown as DecisionReceipt;
}
export function parseSnapshot(value: unknown): CharacterSnapshot | null {
  if (
    !object(value) ||
    !number(value.heroId) ||
    value.heroId <= 0 ||
    !number(value.revision) ||
    !Number.isInteger(value.revision) ||
    value.revision < 1 ||
    typeof value.serverRealUTCAt !== 'string' ||
    !Number.isFinite(Date.parse(value.serverRealUTCAt)) ||
    typeof value.serverSimulationAt !== 'string' ||
    typeof value.simulationPaused !== 'boolean' ||
    !object(value.profile)
  )
    return null;
  const p = value.profile;
  if (
    typeof p.version !== 'string' ||
    !number(p.revision) ||
    typeof p.awarenessDescription !== 'string' ||
    !object(p.traits) ||
    !object(p.beliefs) ||
    !object(p.bond)
  )
    return null;
  for (const id of [
    'compassion',
    'greed',
    'courage',
    'temper',
    'sociability',
    'conscientiousness',
    'independence',
    'pride',
  ])
    if (!number(p.traits[id])) return null;
  const bond = p.bond;
  if (
    !Object.values(p.beliefs).every(number) ||
    !['awareness', 'connection', 'trust'].every((id) => number(bond[id]))
  )
    return null;
  if (value.active !== undefined && value.active !== null) {
    const a = value.active;
    if (
      !object(a) ||
      typeof a.id !== 'string' ||
      typeof a.templateId !== 'string' ||
      typeof a.stageId !== 'string' ||
      typeof a.status !== 'string' ||
      typeof a.deadlineSimAt !== 'string' ||
      !number(a.revision) ||
      !number(a.remainingSeconds) ||
      !Array.isArray(a.participants) ||
      !Array.isArray(a.options)
    )
      return null;
    for (const item of a.participants)
      if (
        !object(item) ||
        typeof item.id !== 'string' ||
        typeof item.side !== 'string' ||
        typeof item.status !== 'string' ||
        typeof item.reachable !== 'boolean' ||
        !number(item.hp)
      )
        return null;
    for (const option of a.options) {
      if (
        !object(option) ||
        typeof option.id !== 'string' ||
        !nullableStrings(option.targetIds) ||
        typeof option.available !== 'boolean' ||
        !nullableStrings(option.reasons) ||
        (option.knownRisk !== undefined && typeof option.knownRisk !== 'string')
      )
        return null;
      if (
        option.cost !== undefined &&
        (!object(option.cost) ||
          !number(option.cost.gold) ||
          !number(option.cost.potions))
      )
        return null;
    }
    if (a.advice !== undefined && a.advice !== null) {
      const advice = parseAdvice(a.advice);
      if (
        !advice ||
        advice.heroId !== value.heroId ||
        advice.command.instanceId !== a.id
      )
        return null;
    }
  }
  const cloned = structuredClone(value) as unknown as CharacterSnapshot;
  if (cloned.active) {
    cloned.active.options = cloned.active.options.map((option) => ({
      ...option,
      targetIds: option.targetIds ?? [],
      reasons: option.reasons ?? [],
    }));
  }
  return cloned;
}
